import type { Restaurante } from '../../database/database';
import type { Propuesta } from '../services/propuestas';
import { haversineKm } from './nearestCiudad';
import { parseLatLngFromGoogleMapsUrl } from './parseGoogleMapsLink';

const NUM = '-?\\d+(?:[.,]\\d+)?';

function parseCoordPair(a: string, b: string): { lat: number; lng: number } | null {
  const lat = parseFloat(String(a).replace(',', '.'));
  const lng = parseFloat(String(b).replace(',', '.'));
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

/** Una sola cadena "latitud, longitud" (coma, punto y coma o espacio entre valores). */
export function parseLatLngPairField(raw: string): { lat: number; lng: number } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const pair = trimmed.match(new RegExp(`^(${NUM})\\s*[,;\\s]\\s*(${NUM})$`));
  if (!pair) return null;
  return parseCoordPair(pair[1], pair[2]);
}

/**
 * Intenta obtener lat/lng desde enlace de mapas, pareja "lat,lng" o dos tokens numéricos.
 */
export function parseLatLngFromUbicacionGps(raw: string): { lat: number; lng: number } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const fromUrl = parseLatLngFromGoogleMapsUrl(trimmed);
  if (fromUrl) return { lat: fromUrl.lat, lng: fromUrl.lng };

  const pair = trimmed.match(new RegExp(`^(${NUM})\\s*[,;\\s]\\s*(${NUM})$`));
  if (pair) {
    const p = parseCoordPair(pair[1], pair[2]);
    if (p) return p;
  }

  return null;
}

/** Coordenadas pegadas en un buscador: pareja, enlace de Maps o texto con paréntesis / barras. */
export function parseCoordsFromSearchQuery(raw: string): { lat: number; lng: number } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const fromGps = parseLatLngFromUbicacionGps(trimmed);
  if (fromGps) return fromGps;
  const normalized = trimmed
    .replace(/[()[\]]/g, '')
    .replace(/[|/]/g, ',')
    .replace(/\s+/g, ' ')
    .trim();
  if (normalized === trimmed) return null;
  return parseLatLngPairField(normalized);
}

function parseRestauranteSedesJson(restaurante: { sedes?: unknown }): Array<{ ubicacion_gps?: string }> {
  const raw = restaurante?.sedes;
  if (!raw) return [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as Array<{ ubicacion_gps?: string }>) : [];
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) return raw as Array<{ ubicacion_gps?: string }>;
  return [];
}

function gpsStringForPropuestaSedeIndex(
  restaurante: Record<string, unknown> | undefined,
  index: number
): string | null {
  if (!restaurante) return null;
  if (index === 0) {
    const g = typeof restaurante.ubicacion_gps === 'string' ? restaurante.ubicacion_gps.trim() : '';
    return g || null;
  }
  const sedesRest = parseRestauranteSedesJson(restaurante as { sedes?: unknown });
  const idx = index - 1;
  const cell = sedesRest[idx];
  if (cell?.ubicacion_gps && typeof cell.ubicacion_gps === 'string') {
    const g = cell.ubicacion_gps.trim();
    if (g) return g;
  }
  return null;
}

/** Cadenas GPS por sede (prioriza `propuestas_sedes.ubicacion_gps`, igual que en la ficha pública). */
function collectUbicacionGpsStringsForPropuesta(propuesta: Propuesta): string[] {
  const rest = propuesta.restaurante as Record<string, unknown> | undefined;
  if (!rest) return [];
  const sedes = propuesta.sedes;
  if (!sedes?.length) {
    const g = gpsStringForPropuestaSedeIndex(rest, 0);
    return g ? [g] : [];
  }
  const ordered = [...sedes].sort((a, b) => (a.sede_index ?? 0) - (b.sede_index ?? 0));
  const out: string[] = [];
  for (let i = 0; i < ordered.length; i++) {
    const fromSede =
      ordered[i]?.ubicacion_gps && typeof ordered[i].ubicacion_gps === 'string'
        ? ordered[i].ubicacion_gps!.trim()
        : '';
    const g = fromSede || gpsStringForPropuestaSedeIndex(rest, i);
    if (g) out.push(g);
  }
  return out;
}

function considerDist(
  best: number | null,
  userLat: number,
  userLng: number,
  lat: number,
  lng: number
): number | null {
  const d = haversineKm(userLat, userLng, lat, lng);
  if (best === null || d < best) return d;
  return best;
}

type BestLabeled = { km: number; label: string; lat: number; lng: number };

function considerBestLabeled(
  best: BestLabeled | null,
  userLat: number,
  userLng: number,
  lat: number,
  lng: number,
  label: string
): BestLabeled | null {
  const d = haversineKm(userLat, userLng, lat, lng);
  const clean = (label || '').trim() || 'Ubicación';
  if (best === null || d < best.km) return { km: d, label: clean, lat, lng };
  return best;
}

export type ClosestUbicacionPropuesta = {
  distanceKm: number;
  ubicacionCorta: string;
  lat: number;
  lng: number;
};

function labelRestaurantePrincipal(propuesta: Propuesta): string {
  const rest = propuesta.restaurante;
  const corta = rest?.sede_ubicacion_corta?.trim();
  if (corta) return corta;
  const ciudad = rest?.ciudades?.nombre?.trim();
  if (ciudad) return ciudad;
  const nom = rest?.nombre_mostrar?.trim() || rest?.nombre_oficial?.trim();
  return nom || 'Ubicación';
}

function labelSedeJsonCell(cell: unknown): string {
  const c = cell as { ubicacion_corta?: string; nombre?: string };
  const u = typeof c.ubicacion_corta === 'string' ? c.ubicacion_corta.trim() : '';
  if (u) return u;
  const n = typeof c.nombre === 'string' ? c.nombre.trim() : '';
  return n || 'Sede';
}

/** Etiquetas humanas alineadas con `collectUbicacionGpsStringsForPropuesta` (mismo orden de sedes). */
function buildSlotLabelsForPropuesta(propuesta: Propuesta): string[] {
  const base = labelRestaurantePrincipal(propuesta);
  const sedes = propuesta.sedes;
  if (!sedes?.length) return [base];
  const ordered = [...sedes].sort((a, b) => (a.sede_index ?? 0) - (b.sede_index ?? 0));
  return ordered.map((s) => {
    const nom = (s.sede_nombre || '').trim();
    const dir = (s.sede_direccion || '').trim();
    if (nom && dir) return `${nom} · ${dir}`;
    if (nom) return nom;
    if (dir) return dir;
    return base;
  });
}

/**
 * Punto visitable más cercano al usuario para esta propuesta + texto corto de esa sede.
 */
export function closestUbicacionKmForPropuesta(
  propuesta: Propuesta,
  userLat: number,
  userLng: number
): ClosestUbicacionPropuesta | null {
  const restRec = propuesta.restaurante as Record<string, unknown> | undefined;
  let best: BestLabeled | null = null;

  if (restRec?.latitud_sede_principal != null && restRec?.longitud_sede_principal != null) {
    const la = Number(restRec.latitud_sede_principal);
    const lo = Number(restRec.longitud_sede_principal);
    if (!Number.isNaN(la) && !Number.isNaN(lo)) {
      best = considerBestLabeled(best, userLat, userLng, la, lo, labelRestaurantePrincipal(propuesta));
    }
  }

  const sedesRest = parseRestauranteSedesJson(restRec ?? {});
  for (const cell of sedesRest) {
    if (cell && (cell as { latitud?: unknown }).latitud != null && (cell as { longitud?: unknown }).longitud != null) {
      const la = Number((cell as { latitud: unknown }).latitud);
      const lo = Number((cell as { longitud: unknown }).longitud);
      if (!Number.isNaN(la) && !Number.isNaN(lo)) {
        best = considerBestLabeled(best, userLat, userLng, la, lo, labelSedeJsonCell(cell));
      }
    }
  }

  const rawList = collectUbicacionGpsStringsForPropuesta(propuesta);
  const slotLabels = buildSlotLabelsForPropuesta(propuesta);
  for (let i = 0; i < rawList.length; i++) {
    const ll = parseLatLngFromUbicacionGps(rawList[i]);
    if (!ll) continue;
    const label =
      slotLabels[i] ?? slotLabels[slotLabels.length - 1] ?? labelRestaurantePrincipal(propuesta);
    best = considerBestLabeled(best, userLat, userLng, ll.lat, ll.lng, label);
  }

  if (!best) return null;
  return {
    distanceKm: best.km,
    ubicacionCorta: best.label,
    lat: best.lat,
    lng: best.lng,
  };
}

/** Primera coordenada de referencia de la propuesta (sede principal o primera sede con GPS). */
export function referenciaCoordsPropuesta(propuesta: Propuesta): { lat: number; lng: number } | null {
  const restRec = propuesta.restaurante as Record<string, unknown> | undefined;
  if (restRec?.latitud_sede_principal != null && restRec?.longitud_sede_principal != null) {
    const la = Number(restRec.latitud_sede_principal);
    const lo = Number(restRec.longitud_sede_principal);
    if (!Number.isNaN(la) && !Number.isNaN(lo)) return { lat: la, lng: lo };
  }
  const sedesRest = parseRestauranteSedesJson(restRec ?? {});
  for (const cell of sedesRest) {
    if (cell && (cell as { latitud?: unknown }).latitud != null && (cell as { longitud?: unknown }).longitud != null) {
      const la = Number((cell as { latitud: unknown }).latitud);
      const lo = Number((cell as { longitud: unknown }).longitud);
      if (!Number.isNaN(la) && !Number.isNaN(lo)) return { lat: la, lng: lo };
    }
  }
  for (const raw of collectUbicacionGpsStringsForPropuesta(propuesta)) {
    const ll = parseLatLngFromUbicacionGps(raw);
    if (ll) return ll;
  }
  return null;
}

/** Mensaje de depuración cuando el voto se rechaza por distancia (modal «Recargar»). */
export function formatMensajeErrorUbicacionVotoUaUrMdd(
  userLat: number,
  userLng: number,
  closest: ClosestUbicacionPropuesta | null
): string {
  const ua = `${userLat.toFixed(6)},${userLng.toFixed(6)}`;
  const ur = closest ? `${closest.lat.toFixed(6)},${closest.lng.toFixed(6)}` : '—';
  const mdd =
    closest != null && Number.isFinite(closest.distanceKm)
      ? String(Math.round(closest.distanceKm * 1000))
      : '—';
  return `Error: UA${ua}UR${ur}MDD${mdd}`;
}

export function minDistanceKmUsuarioPropuesta(
  propuesta: Propuesta,
  userLat: number,
  userLng: number
): number | null {
  return closestUbicacionKmForPropuesta(propuesta, userLat, userLng)?.distanceKm ?? null;
}

export type PropuestaConDistancia = {
  propuesta: Propuesta;
  distanceKm: number;
  ubicacionCorta: string;
};

export function rankPropuestasPorDistancia(
  propuestas: Propuesta[],
  userLat: number,
  userLng: number
): PropuestaConDistancia[] {
  const out: PropuestaConDistancia[] = [];
  for (const p of propuestas) {
    const hit = closestUbicacionKmForPropuesta(p, userLat, userLng);
    if (hit != null) out.push({ propuesta: p, distanceKm: hit.distanceKm, ubicacionCorta: hit.ubicacionCorta });
  }
  out.sort((a, b) => a.distanceKm - b.distanceKm);
  return out;
}

/**
 * Distancia mínima usuario → ficha restaurante (sede principal, sedes JSON, `ubicacion_gps`).
 */
export function minDistanceKmUsuarioRestauranteFicha(
  r: Pick<
    Restaurante,
    'ubicacion_gps' | 'sedes' | 'latitud_sede_principal' | 'longitud_sede_principal'
  >,
  userLat: number,
  userLng: number
): number | null {
  let best: number | null = null;

  if (r.latitud_sede_principal != null && r.longitud_sede_principal != null) {
    const la = Number(r.latitud_sede_principal);
    const lo = Number(r.longitud_sede_principal);
    if (!Number.isNaN(la) && !Number.isNaN(lo)) {
      best = considerDist(best, userLat, userLng, la, lo);
    }
  }

  const sedesRest = parseRestauranteSedesJson(r);
  for (const cell of sedesRest) {
    if (cell && (cell as { latitud?: unknown }).latitud != null && (cell as { longitud?: unknown }).longitud != null) {
      const la = Number((cell as { latitud: unknown }).latitud);
      const lo = Number((cell as { longitud: unknown }).longitud);
      if (!Number.isNaN(la) && !Number.isNaN(lo)) {
        best = considerDist(best, userLat, userLng, la, lo);
      }
    }
    const g =
      typeof (cell as { ubicacion_gps?: string }).ubicacion_gps === 'string'
        ? (cell as { ubicacion_gps: string }).ubicacion_gps.trim()
        : '';
    if (g) {
      const ll = parseLatLngFromUbicacionGps(g);
      if (ll) best = considerDist(best, userLat, userLng, ll.lat, ll.lng);
    }
  }

  const mainGps = typeof r.ubicacion_gps === 'string' ? r.ubicacion_gps.trim() : '';
  if (mainGps) {
    const ll = parseLatLngFromUbicacionGps(mainGps);
    if (ll) best = considerDist(best, userLat, userLng, ll.lat, ll.lng);
  }
  return best;
}

export function rankRestaurantesFichaPorDistancia(
  restaurantes: Restaurante[],
  userLat: number,
  userLng: number
): Array<{ restaurante: Restaurante; distanceKm: number }> {
  const out: Array<{ restaurante: Restaurante; distanceKm: number }> = [];
  for (const r of restaurantes) {
    const km = minDistanceKmUsuarioRestauranteFicha(r, userLat, userLng);
    if (km != null) out.push({ restaurante: r, distanceKm: km });
  }
  out.sort((a, b) => a.distanceKm - b.distanceKm);
  return out;
}

/** Restaurantes de ciudad (BD) ordenados por GPS + enlace opcional a una propuesta pública */
export type RestauranteCercano = {
  restaurante: Restaurante;
  distanceKm: number;
  propuestaEjemploId?: string;
  edicionEjemploId?: number;
};

/** Punto en mapa por establecimiento (sede principal, sedes JSON o `ubicacion_gps`). */
export type EstablecimientoMapPin = {
  id: string;
  restauranteId: string;
  lat: number;
  lng: number;
  label: string;
};

function pinDedupeKey(restauranteId: string, lat: number, lng: number): string {
  return `${restauranteId}|${lat.toFixed(5)}|${lng.toFixed(5)}`;
}

function addEstablecimientoPin(
  out: EstablecimientoMapPin[],
  seen: Set<string>,
  restauranteId: string,
  lat: number,
  lng: number,
  label: string,
  seq: { n: number }
): void {
  if (Number.isNaN(lat) || Number.isNaN(lng)) return;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
  const k = pinDedupeKey(restauranteId, lat, lng);
  if (seen.has(k)) return;
  seen.add(k);
  const id = `${restauranteId}-${seq.n++}`;
  out.push({
    id,
    restauranteId,
    lat,
    lng,
    label: label.trim() || 'Establecimiento',
  });
}

/**
 * Coordenadas públicas para mapa: misma lógica que la distancia a usuario (sin deduplicar entre restaurantes distintos).
 */
export function collectEstablecimientoPins(restaurantes: Restaurante[]): EstablecimientoMapPin[] {
  const out: EstablecimientoMapPin[] = [];
  const seen = new Set<string>();
  const seq = { n: 0 };

  for (const r of restaurantes) {
    const nom = r.nombre_mostrar?.trim() || r.nombre_oficial?.trim() || 'Restaurante';

    if (r.latitud_sede_principal != null && r.longitud_sede_principal != null) {
      addEstablecimientoPin(
        out,
        seen,
        r.id,
        Number(r.latitud_sede_principal),
        Number(r.longitud_sede_principal),
        nom,
        seq
      );
    }

    const sedesRest = parseRestauranteSedesJson(r);
    for (const cell of sedesRest) {
      const sub = labelSedeJsonCell(cell);
      const label = sub && sub !== 'Sede' ? `${nom} · ${sub}` : nom;
      if (cell && (cell as { latitud?: unknown }).latitud != null && (cell as { longitud?: unknown }).longitud != null) {
        addEstablecimientoPin(
          out,
          seen,
          r.id,
          Number((cell as { latitud: unknown }).latitud),
          Number((cell as { longitud: unknown }).longitud),
          label,
          seq
        );
      }
      const g =
        typeof (cell as { ubicacion_gps?: string }).ubicacion_gps === 'string'
          ? (cell as { ubicacion_gps: string }).ubicacion_gps.trim()
          : '';
      if (g) {
        const ll = parseLatLngFromUbicacionGps(g);
        if (ll) addEstablecimientoPin(out, seen, r.id, ll.lat, ll.lng, label, seq);
      }
    }

    const mainGps = typeof r.ubicacion_gps === 'string' ? r.ubicacion_gps.trim() : '';
    if (mainGps) {
      const ll = parseLatLngFromUbicacionGps(mainGps);
      if (ll) addEstablecimientoPin(out, seen, r.id, ll.lat, ll.lng, nom, seq);
    }
  }

  return out;
}

/** Pin de establecimiento más cercano al usuario (Haversine). */
export function nearestEstablecimientoPin(
  user: { lat: number; lng: number },
  pins: EstablecimientoMapPin[]
): EstablecimientoMapPin | null {
  if (pins.length === 0) return null;
  let best = pins[0];
  let bestKm = haversineKm(user.lat, user.lng, best.lat, best.lng);
  for (let i = 1; i < pins.length; i++) {
    const p = pins[i];
    const d = haversineKm(user.lat, user.lng, p.lat, p.lng);
    if (d < bestKm) {
      best = p;
      bestKm = d;
    }
  }
  return best;
}

/** Votación presencial: distancia máxima desde la sede más cercana (metros). */
export const VOTACION_MAX_DISTANCIA_VOTO_METROS = 500;

export const VOTACION_MAX_DISTANCIA_VOTO_KM = VOTACION_MAX_DISTANCIA_VOTO_METROS / 1000;

export function distanciaVotoExcedeMaximoPermitido(
  distanceKm: number | null | undefined,
  maxMetros: number = VOTACION_MAX_DISTANCIA_VOTO_METROS
): boolean {
  const maxKm =
    typeof maxMetros === 'number' && Number.isFinite(maxMetros) && maxMetros > 0
      ? maxMetros / 1000
      : VOTACION_MAX_DISTANCIA_VOTO_KM;
  return typeof distanceKm === 'number' && Number.isFinite(distanceKm) && distanceKm > maxKm;
}

export function resolveVotacionDistanciaMinimaMetros(
  edicion?: { votacion_distancia_minima_metros?: number | null } | null
): number {
  const v = edicion?.votacion_distancia_minima_metros;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return Math.floor(v);
  return VOTACION_MAX_DISTANCIA_VOTO_METROS;
}

/** Texto auxiliar para modales: coordenadas del usuario y distancia a la sede más cercana entre paréntesis. */
export function textoCoordenadasUsuarioYDistanciaSede(
  userLat: number,
  userLng: number,
  closest: { distanceKm: number; ubicacionCorta: string } | null
): string {
  const coords = `${userLat.toFixed(6)}, ${userLng.toFixed(6)}`;
  if (!closest) {
    return `${coords} (sin sedes con GPS para calcular la distancia)`;
  }
  const distTxt =
    closest.distanceKm < 1
      ? `${Math.round(closest.distanceKm * 1000)} m`
      : `${closest.distanceKm.toLocaleString('es-AR', {
          maximumFractionDigits: 2,
          minimumFractionDigits: closest.distanceKm < 10 ? 1 : 0,
        })} km`;
  return `${coords} (${distTxt} a la sede más cercana)`;
}
